/**
 * Display helpers for the Zenoti guest rollups the history and activity
 * syncs write onto `customer.properties.zenoti`.
 *
 * Two kinds of dates live there. Visit and appointment times
 * (`last_visit_at`, `next_appointment_at`) are real instants. Membership,
 * credit, and package dates are calendar days stored at UTC midnight, so
 * they are read in UTC to keep the day from shifting in western time zones.
 */

import { DateTime } from "luxon";
import type { Customer, CustomerEvent } from "@/api/types";
import { formatCurrency } from "@/lib/utils";

/** One open package under `properties.zenoti.packages`. Dates are calendar days. */
export interface ZenotiPackageRollup {
  id: string;
  name: string;
  sessions_total?: number | null;
  sessions_used?: number | null;
  sessions_left?: number | null;
  started_at?: string | null;
  expires_at?: string | null;
  price?: number | null;
}

export interface ZenotiProperties {
  code?: string | null;
  center_name?: string | null;
  created_date?: string | null;
  member_since?: string | null;
  synced_at?: string | null;
  history_synced_at?: string | null;
  preferences?: {
    receive_marketing_sms?: boolean | null;
    receive_marketing_email?: boolean | null;
  } | null;
  visit_count?: number | null;
  last_visit_at?: string | null;
  last_visit_service?: string | null;
  next_appointment_at?: string | null;
  next_appointment_service?: string | null;
  upcoming_appointment_count?: number | null;
  no_show_count?: number | null;
  top_service?: string | null;
  preferred_therapist?: string | null;
  lifetime_spend?: number | null;
  membership_spend?: number | null;
  service_spend?: number | null;
  package_spend?: number | null;
  is_member?: boolean | null;
  membership_name?: string | null;
  membership_status?: string | null;
  membership_type?: string | null;
  membership_started_at?: string | null;
  membership_expires_at?: string | null;
  membership_next_billing_at?: string | null;
  membership_price?: number | null;
  membership_cancelled_at?: string | null;
  membership_credits_left?: number | null;
  membership_credits_expire_at?: string | null;
  became_member_at?: string | null;
  package_count?: number | null;
  package_name?: string | null;
  package_sessions_left?: number | null;
  package_expires_at?: string | null;
  package_names?: string[] | null;
  packages?: ZenotiPackageRollup[] | null;
}

export function zenotiProperties(customer: Customer | undefined): ZenotiProperties | null {
  return (customer?.properties?.zenoti as ZenotiProperties | undefined) ?? null;
}

/** A Zenoti calendar day such as `Nov 2, 2026`. */
export function zenotiDay(value: string | Date): string {
  return DateTime.fromJSDate(new Date(value), { zone: "utc" }).toFormat("DD");
}

/** Start of today in UTC, the zone Zenoti calendar days are stored in. */
function utcToday(now: Date): number {
  return DateTime.fromJSDate(now, { zone: "utc" }).startOf("day").toMillis();
}

/**
 * The membership's next date: when a cancellation takes effect, when it
 * next bills, or when a prepaid plan expires. Null when the plan has none.
 */
export function zenotiMembershipTerm(
  zn: ZenotiProperties,
  now: Date = new Date(),
): { label: string; value: string } | null {
  const today = utcToday(now);
  const upcoming = (value: string) => new Date(value).getTime() >= today;
  if (zn.membership_cancelled_at) {
    return {
      label: upcoming(zn.membership_cancelled_at) ? "Ends" : "Ended",
      value: zenotiDay(zn.membership_cancelled_at),
    };
  }
  if (zn.membership_next_billing_at) {
    const day = zenotiDay(zn.membership_next_billing_at);
    return {
      label: "Renews",
      value: zn.membership_price ? `${day}, ${formatCurrency(zn.membership_price)}` : day,
    };
  }
  if (zn.membership_expires_at) {
    return {
      label: upcoming(zn.membership_expires_at) ? "Expires" : "Expired",
      value: zenotiDay(zn.membership_expires_at),
    };
  }
  return null;
}

/** Banked membership credits with the soonest expiry, e.g. `2, expire Mar 5, 2027`. */
export function zenotiCredits(zn: ZenotiProperties): string | null {
  const left = zn.membership_credits_left ?? 0;
  if (left <= 0) return null;
  return zn.membership_credits_expire_at
    ? `${left}, expire ${zenotiDay(zn.membership_credits_expire_at)}`
    : String(left);
}

export interface ZenotiPackageItem {
  id: string;
  name: string;
  /** Null when the rollup carries only the package name. */
  sessionsLeft: number | null;
  sessionsTotal: number | null;
  startedAt: string | null;
  expiresAt: string | null;
  price: number | null;
}

/**
 * Open packages, soonest expiry first. Rollups without `packages` carry
 * only the names, so those items have no sessions or dates.
 */
export function zenotiPackageList(zn: ZenotiProperties): ZenotiPackageItem[] {
  if (Array.isArray(zn.packages)) {
    return zn.packages.map((pkg) => ({
      id: pkg.id,
      name: pkg.name,
      sessionsLeft: pkg.sessions_left ?? null,
      sessionsTotal: pkg.sessions_total || null,
      startedAt: pkg.started_at ?? null,
      expiresAt: pkg.expires_at ?? null,
      price: pkg.price ?? null,
    }));
  }
  return (zn.package_names ?? []).map((name) => ({
    id: name,
    name,
    sessionsLeft: null,
    sessionsTotal: null,
    startedAt: null,
    expiresAt: null,
    price: null,
  }));
}

/** `8 of 10 left`, `8 left`, or null when the balance is unknown. */
export function zenotiSessionsLabel(pkg: ZenotiPackageItem): string | null {
  if (pkg.sessionsLeft == null) return null;
  return pkg.sessionsTotal != null
    ? `${pkg.sessionsLeft} of ${pkg.sessionsTotal} left`
    : `${pkg.sessionsLeft} left`;
}

/** Status values understood by the sidebar's reservation status badge. */
export type ZenotiVisitStatus = "upcoming" | "booked" | "checked in" | "cancelled" | "no show";

/** One appointment group rebuilt from its lifecycle events. */
export interface ZenotiVisitItem {
  id: string;
  name: string;
  services: string[];
  addons: string[];
  therapists: string[];
  room: string | null;
  startAt: string;
  endAt: string | null;
  price: number;
  status: ZenotiVisitStatus;
  isRebooking: boolean;
}

const ZENOTI_APPOINTMENT_OUTCOMES: Record<string, ZenotiVisitStatus | null> = {
  appointment_booked: null,
  appointment_attended: "checked in",
  appointment_cancelled: "cancelled",
  appointment_no_show: "no show",
};

function asDateString(value: unknown): string | null {
  if (!value) return null;
  const date = new Date(value as string | Date);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
}

/**
 * Visits from the Zenoti appointment events. Each appointment group files
 * `appointment_booked` plus at most one outcome under the same `id`; a
 * group with no outcome is upcoming until its start time passes. Upcoming
 * visits come soonest first, history newest first.
 */
export function zenotiVisitsFromEvents(
  events: CustomerEvent[] | undefined,
  now: Date = new Date(),
): { upcoming: ZenotiVisitItem[]; history: ZenotiVisitItem[] } {
  const visits = new Map<string, ZenotiVisitItem>();
  for (const event of events ?? []) {
    if (!(event.event in ZENOTI_APPOINTMENT_OUTCOMES)) continue;
    const props = (event.properties ?? {}) as Record<string, unknown>;
    const id = typeof props.id === "string" ? props.id : null;
    const startAt = asDateString(props.startDateTime);
    if (!id || !startAt) continue;
    const therapists = asStrings(props.therapists);
    const visit = visits.get(id) ?? {
      id,
      name: (props.appointmentName as string) || "Appointment",
      services: asStrings(props.services),
      addons: asStrings(props.addons),
      therapists: therapists.length ? therapists : asStrings([props.therapist]),
      room: (props.room as string) || null,
      startAt,
      endAt: asDateString(props.endDateTime),
      price: Number(props.price) || 0,
      status: "booked" as ZenotiVisitStatus,
      isRebooking: props.isRebooking === true,
    };
    visit.status = ZENOTI_APPOINTMENT_OUTCOMES[event.event] ?? visit.status;
    visits.set(id, visit);
  }

  const upcoming: ZenotiVisitItem[] = [];
  const history: ZenotiVisitItem[] = [];
  for (const visit of visits.values()) {
    if (visit.status === "booked" && new Date(visit.startAt) > now) {
      upcoming.push({ ...visit, status: "upcoming" });
    } else {
      history.push(visit);
    }
  }
  upcoming.sort((a, b) => a.startAt.localeCompare(b.startAt));
  history.sort((a, b) => b.startAt.localeCompare(a.startAt));
  return { upcoming, history };
}

export interface ZenotiPurchaseItem {
  id: string;
  kind: "package" | "visit";
  title: string;
  date: string;
  amount: number;
}

/** Paid package purchases and paid visits from the Zenoti events, newest first. */
export function zenotiPurchasesFromEvents(
  events: CustomerEvent[] | undefined,
): ZenotiPurchaseItem[] {
  const purchases = new Map<string, ZenotiPurchaseItem>();
  for (const event of events ?? []) {
    const props = (event.properties ?? {}) as Record<string, unknown>;
    const amount = Number(props.price) || 0;
    const id = typeof props.id === "string" ? props.id : null;
    if (!id || amount <= 0) continue;
    if (event.event === "package_purchased") {
      const date = asDateString(event.createdAt);
      if (date) {
        purchases.set(id, {
          id,
          kind: "package",
          title: (props.packageName as string) || "Package",
          date,
          amount,
        });
      }
    } else if (event.event === "appointment_attended") {
      const date = asDateString(props.startDateTime);
      if (date) {
        purchases.set(id, {
          id,
          kind: "visit",
          title: (props.appointmentName as string) || "Appointment",
          date,
          amount,
        });
      }
    }
  }
  return [...purchases.values()].sort((a, b) => b.date.localeCompare(a.date));
}

/** `GET /customer-events` params for a contact's Zenoti timeline. */
export function zenotiEventsQuery(gymId: string, customerId: string) {
  return { gym: gymId, customer: customerId, type: "zenoti", sort: "-createdAt", limit: 200 };
}
